/** Which session today, and what it contains after phase and de-load adjustment. */
import { PLAN, META } from './data.ts';
import type { Exercise, LiftSession, PlanState, RunSession, RunBlock } from './types.ts';
import { phaseFor, runWeekFor, isDeload } from './phase.ts';

export type SessionId = 'liftA' | 'liftB' | 'run0' | 'run1' | 'run2' | 'recWed' | 'recSun';
export type SessionKind = 'lift' | 'run' | 'recovery';

/** meta.scheduleByDow uses recoveryWed/recoverySun; the key format uses recWed/recSun. */
const SCHEDULE_ALIASES: Record<string, SessionId> = {
  liftA: 'liftA', liftB: 'liftB', run0: 'run0', run1: 'run1', run2: 'run2',
  recoveryWed: 'recWed', recoverySun: 'recSun',
};

export function sessionIdForDow(dow: number): SessionId {
  const raw = META.scheduleByDow[String(dow)];
  const id = SCHEDULE_ALIASES[raw];
  if (!id) throw new Error(`plan-data meta.scheduleByDow[${dow}] is unrecognised: ${raw}`);
  return id;
}

export function kindOf(id: SessionId): SessionKind {
  if (id === 'liftA' || id === 'liftB') return 'lift';
  if (id === 'recWed' || id === 'recSun') return 'recovery';
  return 'run';
}

/** A unilateral prescription is twice the listed sets of real work, but the tick UI
 *  still renders `s` targets, not `s * 2` (section 4). */
export function isUnilateral(ex: Exercise): boolean {
  return /each side|each leg|each arm/.test(ex.r);
}
export function realWorkingSets(ex: Exercise): number {
  return ex.s * (isUnilateral(ex) ? 2 : 1);
}

export const DELOAD_SET_FLOOR = 2;

/**
 * De-load: "every lifting exercise drops one set, floored at 2, at the same weight",
 * and only when not holding.
 *
 * The floor is load-bearing, not cosmetic. The only exercises prescribed 2 sets are the
 * Pallof press and dead bug in all four phases - the anti-rotation and anti-extension
 * work section 3 requires in every phase. Without the floor a de-load week would cut the
 * user's back insurance to a single set.
 */
export function deloadedSets(sets: number): number {
  return Math.max(DELOAD_SET_FLOOR, sets - 1);
}

export interface ResolvedLift {
  id: 'liftA' | 'liftB';
  phase: number;
  name: string;
  focus: string;
  ex: Array<Exercise & { plannedSets: number; realSets: number; unilateral: boolean }>;
  deloaded: boolean;
  totalTickTargets: number;
}

export function liftSessionFor(
  state: PlanState, startDate: string, week: number, id: 'liftA' | 'liftB',
): ResolvedLift {
  const p = phaseFor(state, startDate, week);
  const src: LiftSession = PLAN.lifts[String(p.eff)][id === 'liftA' ? 'A' : 'B'];
  // De-load applies on a de-load week, but not while holding: a hold is already
  // serving an earlier, lighter build week.
  const deloaded = isDeload(week) && !p.hold;
  const ex = src.ex.map((e) => {
    const plannedSets = deloaded ? deloadedSets(e.s) : e.s;
    const unilateral = isUnilateral(e);
    return { ...e, plannedSets, realSets: plannedSets * (unilateral ? 2 : 1), unilateral };
  });
  return {
    id, phase: p.eff, name: src.name, focus: src.focus, ex, deloaded,
    totalTickTargets: ex.reduce((s, e) => s + e.plannedSets, 0),
  };
}

export interface ResolvedRun {
  id: 'run0' | 'run1' | 'run2';
  slot: number;
  week: number;             // the week actually served, which a hold can change
  title: string;
  note: string;
  /** Warm-up and cool-down are prepended and appended by the app, not by the data,
   *  and are NOT counted as running minutes (section 4). */
  warmUp: Exercise;
  coolDown: Exercise;
  blocks: RunBlock[];
  runningMinutes: number;
  totalTickTargets: number;
}

export function runSessionFor(
  state: PlanState, startDate: string, week: number, id: 'run0' | 'run1' | 'run2',
): ResolvedRun {
  const slot = Number(id.slice(3));
  const served = runWeekFor(state, startDate, week);
  const src: RunSession = PLAN.runs[String(served)][slot];
  return {
    id, slot, week: served, title: src.title, note: src.note,
    warmUp: PLAN.runWarmUp, coolDown: PLAN.runCoolDown,
    blocks: src.blocks,
    runningMinutes: src.blocks.reduce((s, b) => s + b.s * b.min, 0),
    totalTickTargets: src.blocks.reduce((s, b) => s + b.s, 0),
  };
}

export interface ResolvedRecovery {
  id: 'recWed' | 'recSun';
  name: string; focus: string; ex: Exercise[]; totalTickTargets: number;
}

export function recoverySessionFor(id: 'recWed' | 'recSun'): ResolvedRecovery {
  const src = id === 'recWed' ? PLAN.recovery.wed : PLAN.recovery.sun;
  return { id, name: src.name, focus: src.focus, ex: src.ex,
           totalTickTargets: src.ex.reduce((s, e) => s + e.s, 0) };
}

export type ResolvedSession = ResolvedLift | ResolvedRun | ResolvedRecovery;

export function sessionFor(
  state: PlanState, startDate: string, week: number, id: SessionId,
): ResolvedSession {
  switch (kindOf(id)) {
    case 'lift': return liftSessionFor(state, startDate, week, id as 'liftA' | 'liftB');
    case 'run': return runSessionFor(state, startDate, week, id as 'run0' | 'run1' | 'run2');
    case 'recovery': return recoverySessionFor(id as 'recWed' | 'recSun');
  }
}

/** Total weekly running minutes for the week actually served. */
export const RUN_IDS = ['run0', 'run1', 'run2'] as const;

export function weeklyRunningMinutes(state: PlanState, startDate: string, week: number): number {
  return RUN_IDS.reduce(
    (total, id) => total + runSessionFor(state, startDate, week, id).runningMinutes, 0);
}
